import React, { useState, useRef, useEffect } from 'react';
import { 
  ArrowLeft, 
  MoreHorizontal, 
  Share2, 
  Flag, 
  BellOff, 
  MessageCircle, 
  Heart, 
  ThumbsUp, 
  Send, 
  Smile, 
  Image, 
  AtSign, 
  ChevronDown, 
  ChevronUp,
  User,
  Clock,
  Eye,
  BarChart3
} from 'lucide-react';
import { Post, Comment } from '../../types';
import { showToast } from '../../utils/toast';
import { useAuth } from '../../hooks/useAuth';
import ReplyComposer from './ReplyComposer';
import PostCard from './PostCard';
import ReplyCard from './ReplyCard';
import PollDisplay from './PollDisplay';
import { useProfile } from '../../hooks/useProfile';

interface DiscussionViewProps {
  post: Post;
  onClose: () => void;
  onPostInteraction: (postId: string, action: string) => void;
  onAddComment: (postId: string, content: string) => Promise<void>;
  onFollowExpert: (expertId: string) => void;
  onSharePost: (post: Post) => void;
  onReportPost: (postId: string, reason: string) => void;
  onViewProfile: (userId: string) => void;
}

const DiscussionView: React.FC<DiscussionViewProps> = ({
  post,
  onClose,
  onPostInteraction,
  onAddComment,
  onFollowExpert,
  onSharePost,
  onReportPost,
  onViewProfile
}) => {
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [expandedReplies, setExpandedReplies] = useState<Set<string>>(new Set());
  const [showRelatedContent, setShowRelatedContent] = useState(true);
  const [isTyping, setIsTyping] = useState<{[key: string]: boolean}>({});
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);
  
  const { user } = useAuth();
  const { profile } = useProfile();
  
  // Scroll to bottom when new comments are added
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [post.comments.length]);
  
  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (optionsRef.current && !optionsRef.current.contains(event.target as Node)) {
        setShowOptions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Simulate typing indicators for demo purposes
  useEffect(() => {
    const typingInterval = setInterval(() => {
      if (post.comments.length > 0 && Math.random() > 0.7) {
        const randomCommentIndex = Math.floor(Math.random() * post.comments.length);
        const randomCommentId = post.comments[randomCommentIndex].id;
        
        setIsTyping(prev => ({
          ...prev,
          [randomCommentId]: true
        }));
        
        // Stop typing after a random time
        setTimeout(() => {
          setIsTyping(prev => ({
            ...prev,
            [randomCommentId]: false
          }));
        }, Math.random() * 5000 + 2000);
      }
    }, 10000);
    
    return () => clearInterval(typingInterval);
  }, [post.comments]);
  
  const handleSubmitReply = async () => {
    if (!replyContent.trim()) return;
    
    if (!user) {
      showToast.auth.authRequired('Please sign in to join the discussion');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onAddComment(post.id, replyContent);
      setReplyContent('');
      showToast.community.commentAdded();
    } catch (error) {
      console.error('Error adding comment:', error);
      showToast.system.error('Failed to add comment. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const handleReportPost = () => {
    if (!reportReason.trim()) {
      showToast.system.error('Please provide a reason for reporting this post');
      return;
    }
    
    onReportPost(post.id, reportReason);
    setShowReportModal(false);
    setReportReason('');
    showToast.community.postReported();
  };
  
  const toggleReplyExpansion = (replyId: string) => {
    setExpandedReplies(prev => {
      const newSet = new Set(prev);
      if (newSet.has(replyId)) {
        newSet.delete(replyId);
      } else {
        newSet.add(replyId);
      }
      return newSet;
    });
  };
  
  // Group comments by parent (for threading)
  const topLevelComments = post.comments.filter(comment => !comment.parentId);
  const commentReplies = post.comments.reduce((acc, comment) => {
    if (comment.parentId) {
      if (!acc[comment.parentId]) {
        acc[comment.parentId] = [];
      }
      acc[comment.parentId].push(comment);
    }
    return acc;
  }, {} as {[key: string]: Comment[]});
  
  // Get top contributors
const topContributors = Object.values((post.comments || []).reduce((acc, comment) => {
  const authorId = comment.authorId;
  if (!acc[authorId]) {
    acc[authorId] = {
      id: authorId,
      name: comment.authorName,
      isExpert: comment.isExpert,
      count: 0,
    };
  }
  acc[authorId].count++;
  return acc;
}, {} as { [key: string]: { id: string; name: string; isExpert: boolean; count: number } }))
  .sort((a, b) => b.count - a.count)
  .slice(0, 3);

  
  // Calculate total reactions
  const totalReactions = post.reactions.length;
  
  // Find most helpful reply (most reactions)
  const mostHelpfulReply = [...post.comments].sort((a, b) => 
    (b.reactions?.length || 0) - (a.reactions?.length || 0)
  )[0];
  
  return (
    <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex flex-col">
      {/* Header */}
      <div className="sticky top-0 z-10 glass border-b border-gray-200 dark:bg-gray-900/90 dark:border-gray-700">
        <div className="container-responsive py-4 flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Go back"
            >
              <ArrowLeft className="text-gray-600 dark:text-gray-300" size={20} />
            </button>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Discussion</h2>
          </div>
          
          <div className="relative" ref={optionsRef}>
            <button
              onClick={() => setShowOptions(!showOptions)}
              className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="More options"
            >
              <MoreHorizontal className="text-gray-600 dark:text-gray-300" size={20} />
            </button>
            
            {showOptions && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 z-10 animate-scale-in">
                <div className="py-1">
                  <button
                    onClick={() => {
                      onSharePost(post);
                      setShowOptions(false);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                  >
                    <Share2 size={16} />
                    <span>Share Post</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowReportModal(true);
                      setShowOptions(false);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                  >
                    <Flag size={16} />
                    <span>Report Post</span>
                  </button>
                  <button
                    onClick={() => {
                      showToast.system.success('Thread muted. You won\'t receive notifications for this discussion.');
                      setShowOptions(false);
                    }}
                    className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 w-full text-left"
                  >
                    <BellOff size={16} />
                    <span>Mute Thread</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex flex-1 overflow-hidden">
        {/* Main Content */}
        <div className="flex-1 overflow-y-auto scrollbar-custom">
          <div className="container-responsive py-6">
            {/* Main Post */}
            <div className="mb-6">
              <PostCard 
                post={post} 
                onPostInteraction={onPostInteraction}
                onSharePost={onSharePost}
                onViewProfile={onViewProfile}
              />
              
              {/* Poll Display */}
              {post.postType === 'poll' && post.pollOptions && post.pollOptions.length > 0 && (
                <div className="mt-4">
                  <PollDisplay 
                    post={post} 
                    onVote={(optionIndex) => onPostInteraction(post.id, `vote:${optionIndex}`)} 
                  />
                </div>
              )}
            </div>
            
            {/* Discussion Stats */}
            <div className="flex items-center justify-between mb-6 px-4 py-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <MessageCircle size={16} className="text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{post.comments.length} replies</span>
                </div>
                <div className="flex items-center space-x-1">
                  <Heart size={16} className="text-gray-500 dark:text-gray-400" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">{totalReactions} reactions</span>
                </div>
              </div>
              
              <button
                onClick={() => setShowRelatedContent(!showRelatedContent)}
                className="flex items-center space-x-1 text-sm text-gray-700 dark:text-gray-300"
              >
                {showRelatedContent ? (
                  <>
                    <ChevronUp size={16} />
                    <span>Hide sidebar</span>
                  </>
                ) : (
                  <>
                    <ChevronDown size={16} />
                    <span>Show sidebar</span>
                  </>
                )}
              </button>
            </div>
            
            {/* AI Summary for long threads */}
            {post.comments.length > 5 && (
              <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-100 dark:border-blue-800 rounded-xl">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                    <Sparkles className="text-white" size={16} />
                  </div>
                  <div>
                    <h3 className="font-medium text-blue-800 dark:text-blue-300 mb-1">Discussion Summary</h3>
                    <p className="text-sm text-blue-700 dark:text-blue-400">
                      This discussion focuses on {post.category} with several experts weighing in. Key points include medication recommendations, lifestyle changes, and preventive measures. Most participants agree that regular check-ups are essential.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Comments Section */}
            <div className="space-y-4 mb-20">
              <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-4">
                Responses ({post.comments.length})
              </h3>
              
              {/* Most Helpful Reply (if exists and has reactions) */}
              {mostHelpfulReply && mostHelpfulReply.reactions?.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center space-x-2 mb-2">
                    <ThumbsUp size={16} className="text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-green-700 dark:text-green-400">Most Helpful Response</span>
                  </div>
                  <ReplyCard
                    comment={mostHelpfulReply}
                    isExpanded={expandedReplies.has(mostHelpfulReply.id)}
                    onToggleExpand={() => toggleReplyExpansion(mostHelpfulReply.id)}
                    onReply={(commentId) => {
                      setReplyContent(`@${mostHelpfulReply.authorName} `);
                      // Focus on reply input
                      const replyInput = document.getElementById('reply-input');
                      if (replyInput) {
                        replyInput.focus();
                      }
                    }}
                    onReaction={(commentId, reaction) => {
                      // Handle reaction to comment
                      showToast.community.reactionAdded(reaction);
                    }}
                    onViewProfile={onViewProfile}
                    highlightStyle="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800"
                  />
                </div>
              )}
              
              {/* Top Level Comments */}
              {topLevelComments.length > 0 ? (
                topLevelComments.map(comment => (
                  <div key={comment.id} className="space-y-3">
                    <ReplyCard
                      comment={comment}
                      isExpanded={expandedReplies.has(comment.id)}
                      onToggleExpand={() => toggleReplyExpansion(comment.id)}
                      onReply={(commentId) => {
                        setReplyContent(`@${comment.authorName} `);
                        // Focus on reply input
                        const replyInput = document.getElementById('reply-input');
                        if (replyInput) {
                          replyInput.focus();
                        }
                      }}
                      onReaction={(commentId, reaction) => {
                        // Handle reaction to comment
                        showToast.community.reactionAdded(reaction);
                      }}
                      onViewProfile={onViewProfile}
                    />
                    
                    {/* Nested Replies */}
                    {commentReplies[comment.id] && commentReplies[comment.id].length > 0 && (
                      <div className="ml-8 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-3">
                        {commentReplies[comment.id].map(reply => (
                          <ReplyCard
                            key={reply.id}
                            comment={reply}
                            isExpanded={expandedReplies.has(reply.id)}
                            onToggleExpand={() => toggleReplyExpansion(reply.id)}
                            onReply={(commentId) => {
                              setReplyContent(`@${reply.authorName} `);
                              // Focus on reply input
                              const replyInput = document.getElementById('reply-input');
                              if (replyInput) {
                                replyInput.focus();
                              }
                            }}
                            onReaction={(commentId, reaction) => {
                              // Handle reaction to comment
                              showToast.community.reactionAdded(reaction);
                            }}
                            onViewProfile={onViewProfile}
                            isNested={true}
                          />
                        ))}
                      </div>
                    )}
                    
                    {/* Typing indicator */}
                    {isTyping[comment.id] && (
                      <div className="ml-8 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                        <div className="flex items-center space-x-2 text-gray-500 dark:text-gray-400 text-sm">
                          <div className="flex space-x-1">
                            <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                            <div className="w-2 h-2 bg-gray-400 dark:bg-gray-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                          </div>
                          <span>Someone is typing a reply...</span>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-8">
                  <MessageCircle className="mx-auto text-gray-400 dark:text-gray-600 mb-3" size={32} />
                  <p className="text-gray-600 dark:text-gray-400 mb-2">No responses yet</p>
                  <p className="text-sm text-gray-500 dark:text-gray-500">Be the first to join the discussion!</p>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>
          </div>
        </div>
        
        {/* Sidebar (Desktop Only) */}
        {showRelatedContent && (
          <div className="hidden lg:block w-80 border-l border-gray-200 dark:border-gray-700 p-4 overflow-y-auto scrollbar-custom">
            <div className="space-y-6">
              {/* Post Stats */}
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Post Stats</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Replies</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{post.comments.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Reactions</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{totalReactions}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Views</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">124</span>
                  </div>
                </div>
              </div>
              
              {/* Top Contributors */}
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Top Contributors</h3>
                <div className="space-y-3">
                  {Object.values(topContributors).map((contributor) => (
                    <button
                      key={contributor.id}
                      onClick={() => onViewProfile(contributor.id)}
                      className="flex items-center space-x-2 w-full p-2 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <div className="avatar-sm">
                        {contributor.name.charAt(0)}
                      </div>
                      <div className="flex-1 text-left">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900 dark:text-gray-100">{contributor.name}</p>
                          {contributor.isExpert && (
                            <span className="badge-success text-xs">Expert</span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{contributor.count} replies</p>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Related Posts */}
              <div>
                <h3 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Related Discussions</h3>
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-750 cursor-pointer transition-colors">
                      <p className="font-medium text-gray-900 dark:text-gray-100 text-sm mb-1">
                        {post.category === 'mental-health' 
                          ? 'Dealing with anxiety during exams' 
                          : post.category === 'nutrition'
                          ? 'Best foods for boosting immunity'
                          : 'Managing chronic pain naturally'}
                      </p>
                      <div className="flex items-center space-x-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center space-x-1">
                          <MessageCircle size={12} />
                          <span>{5 + i * 3}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <Heart size={12} />
                          <span>{8 + i * 2}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Shared Wisdom Card */}
              <div className="p-4 bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/30 dark:to-blue-900/30 rounded-xl border border-purple-100 dark:border-purple-800">
                <h3 className="font-medium text-purple-900 dark:text-purple-300 mb-2">Shared Wisdom</h3>
                <p className="text-sm text-purple-800 dark:text-purple-200 mb-3">
                  {post.category === 'mental-health'
                    ? 'Regular mindfulness practice can significantly reduce anxiety symptoms and improve overall mental wellbeing.'
                    : post.category === 'nutrition'
                    ? 'A balanced diet rich in fruits, vegetables, and whole grains provides essential nutrients for optimal health.'
                    : 'Consistent physical activity is key to maintaining good health and preventing chronic diseases.'}
                </p>
                <button className="text-xs font-medium text-purple-700 dark:text-purple-300 hover:text-purple-800 dark:hover:text-purple-200 transition-colors">
                  Save to Health Notes
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Reply Composer (Sticky at Bottom) */}
      <div className="sticky bottom-0 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 z-10">
        <ReplyComposer
          value={replyContent}
          onChange={setReplyContent}
          onSubmit={handleSubmitReply}
          isSubmitting={isSubmitting}
          currentUser={profile}
        />
      </div>
      
      {/* Report Modal */}
      {showReportModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Report Post</h3>
            </div>
            <div className="p-6">
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Please let us know why you're reporting this post. Your report will be reviewed by our team.
              </p>
              <textarea
                value={reportReason}
                onChange={(e) => setReportReason(e.target.value)}
                placeholder="Describe the issue with this post..."
                className="textarea-base w-full mb-4"
                rows={4}
              />
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowReportModal(false)}
                  className="btn-ghost"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReportPost}
                  className="btn-primary"
                >
                  Submit Report
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DiscussionView;